/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from "vuex";
import error from '../../../../common/nodejs/errors';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { REQUEST_STATUS_COMPLETED, REQUEST_STATUS_FAILED } from '../../../../share/util/api/polling';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  CONFIRM: "confirm",
  EXECUTE: "execute",
  SUCCESS: "success",
  FAILED: "failed",
  STOP_FAILED: "stop_failed",
  ERROR: "error"
};

/** 現在セッション */
const SESSION_PROPERTY = {
  DEFAULT: 0,
  NON_DEFAULT: 1
};

export default {
  name: "requirement60028",
  data() {
    return {
      phase: PHASE.CONFIRM,
      isLoading: false,
      sessionProperty: SESSION_PROPERTY.DEFAULT,
      // ダイアログフラグ
      modal_requirement_60028: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: "", required: true },
    workSupportId: { type: Number, default: "", required: true }
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),
    /**
     * ダイアログのヘッダー色
     */
    headerColor() {
      switch (this.phase) {
        case PHASE.FAILED:
        case PHASE.STOP_FAILED:
        case PHASE.ERROR:
          return "danger";
        case PHASE.SUCCESS:
          return "success";
        case PHASE.CONFIRM:
        case PHASE.EXECUTE:
        default:
          return "secondary";
      }
    },
    /**
     * ダイアログのヘッダー文言
     */
    headerTitle() {
      // 確認
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 実行中
      const ExecutionTitle = 'individual.common_title_execution';
      // 正常終了
      const SuccessTitle = 'message.common_title_success';
      // データ取得エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        confirm: ConfirmationTitle,
        execute: ExecutionTitle,
        success: SuccessTitle,
        failed: ErrorTitle,
        stop_failed: ErrorTitle,
        error: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /**
     * ダイアログの表示文言
     */
    workSupportMessage() {
      const messageMap = {
        confirm: `individual.label_perform_temporary_vehicle_start`,
        execute: `individual.label_wait`,
        success: `individual.label_temporary_vehicle_start_performed`,
        failed: `individual.label_failed_perform_temporary_vehicle_start`,
        stop_failed: `individual.label_failed_perform_temporary_vehicle_stop`,
        error: 'individual.error_communication_error'
      };
      return this.$t(messageMap[this.phase]);
    },

    // 「OK」ボタンの表示確認
    showButtonOk() {
      const showOkButtonMap = {
        confirm: true,
        success: true,
        failed: true,
        stop_failed: true,
        error: true
      };
      let result = showOkButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    // 「キャンセル」ボタンの表示判定
    showButtonCancel() {
      const showCancelButtonMap = {
        confirm: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    }
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession"]),

    /** 初期化処理 */
    init() {
      this.modal_requirement_60028 = true;
      this.sessionProperty = SESSION_PROPERTY.DEFAULT;
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit("close-wizard");
      this.modal_requirement_60028 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** NonDefaultSession遷移 */
    async standby() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        // NonDefaultSession遷移
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
          // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 成功
            // 現在セッション更新（NonDefaultSession）
            this.sessionProperty = SESSION_PROPERTY.NON_DEFAULT;
            // 実行処理
            await this.execute();
            break;
          case REQUEST_STATUS_FAILED:
          default:
            // エラー
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行処理 */
    async execute() {
      try {
        this.isLoading = true;
        const systemId = individualDiagnose.getCurrentSystemId(this);
        const token = session.getToken();

        // 実行
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
          // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // responseが存在しない場合、共通エラーに遷移
        if (!response) {
          this.showUnexpectedError();
          return;
        }

        switch (response.data.status) {
          case REQUEST_STATUS_COMPLETED:
            // 成功
            this.phase = PHASE.SUCCESS;
            break;
          case REQUEST_STATUS_FAILED:
            switch (response.data.async_code) {
              case error.WSP_60028_EXECUTE_ERROR:
                // 実行エラー
                this.phase = PHASE.FAILED;
                break;
              case error.WSP_60028_COMMUNICATION_ERROR:
              default:
                // エラー
                this.showUnexpectedError();
                break;
            }
            break;
          default:
            // エラー
            this.showUnexpectedError();
            break;
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 停止処理 */
    async stop() {
      let isSuccess = false;
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();

      // 停止
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'stop'
        },
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
        // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );

      // responseが存在しない場合、共通エラーに遷移
      if (!response) {
        this.showUnexpectedError();
        return isSuccess;
      }
      switch (response.data.status) {
        case REQUEST_STATUS_COMPLETED:
          isSuccess = true;
          break;
        case REQUEST_STATUS_FAILED:
          switch (response.data.async_code) {
            case error.WSP_60028_STOP_ERROR:
              // 停止エラー
              this.phase = PHASE.STOP_FAILED;
              break;
            case error.WSP_60028_COMMUNICATION_ERROR:
            default:
              // エラー
              this.showUnexpectedError();
              break;
          }
          break;
        default:
          // エラー
          this.showUnexpectedError();
          break;
      }
      return isSuccess;
    },

    /** terminate処理 */
    async terminate() {
      let isSuccess = false;
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();

      // 停止
      const response = await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'terminate'
        },
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
        // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );

      // responseが存在しない場合、共通エラーに遷移
      if (!response) {
        this.showUnexpectedError();
        return isSuccess;
      }

      switch (response.data.status) {
        case REQUEST_STATUS_COMPLETED:
          // 成功
          isSuccess = true;
          // 現在セッション更新（DefaultSession）
          this.sessionProperty = SESSION_PROPERTY.DEFAULT;
          break;
        case REQUEST_STATUS_FAILED:
          if (this.phase === PHASE.ERROR) {
            // 共通エラー画面の場合は、ウィザードを閉じる
            break;
          } else {
            // 共通エラー
            this.showUnexpectedError();
            break;
          }
        default:
          // エラー
          this.showUnexpectedError();
          break;
      }
      return isSuccess;
    },

    /** dispose処理 */
    async dispose() {
      const systemId = individualDiagnose.getCurrentSystemId(this);
      const token = session.getToken();

      return await individualWorkSupportUtil.pollingWspApi(
        this,
        this.netAppId,
        systemId,
        this.workSupportId,
        {
          id_token: token.id_token,
          type: 'dispose'
        },
        // エラー発生のコールバックを実装する。
        // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列、もしくは独自定義した文字列が返ってくる。
        // 必要に応じて、errorType に応じてメッセージを変えるといったことも可能。
        (errorType) => { errorType; this.showUnexpectedError(); },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
        (result) => { this.validateErrorResult(result); },
      );
    },

    /** 確認画面「OK」押下 */
    clickConfirmOk() {
      // NonDefaultSession遷移
      this.phase = PHASE.EXECUTE;
      this.standby();
    },

    /** 成功画面、失敗画面「OK」押下 */
    async clickOk() {
      try {
        this.isLoading = true;
        if (!await this.stop()) {
          return;
        }
        if (!await this.terminate()) {
          return;
        }
        await this.dispose();
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** 停止失敗画面「OK」押下 */
    async clickStopFailedOk() {
      try {
        this.isLoading = true;
        if (!await this.terminate()) {
          return;
        }
        await this.dispose();
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** 共通エラー画面「OK」押下 */
    async clickErrorOk() {
      try {
        this.isLoading = true;
        if (this.sessionProperty === SESSION_PROPERTY.NON_DEFAULT) {
          await this.terminate();
        }
        await this.dispose();
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：エラー表示
      this.phase = PHASE.ERROR;
    },

    /** 「OK」ボタンの押下処理*/
    onOkClicked() {
      switch (this.phase) {
        case 'confirm':
          this.clickConfirmOk();
          break;
        case 'success':
        case 'failed':
          this.clickOk();
          break;
        case 'stop_failed':
          this.clickStopFailedOk();
          break;
        case 'error':
          this.clickErrorOk();
          break;
        default:
          break;
      }
    },

    /** 「キャンセル」ボタンの押下処理 */
    onCancelClicked() {
      this.closeWizard();
    }
  },

  /**
   * DOM作成後実行処理
   */
  async mounted() {
    this.init();
  }
};
